/**
 * Sysadmin Catalog → Tools → New (wizard) — Phase 1 feature expansion.
 *
 * Mirrors the tenant Create Tool wizard at `/studio/tools/new`:
 *   - Identity card (slug, display name, description, category)
 *   - Source type radio (builtin / mcp / openapi / http / database / custom_function)
 *   - Source-type-specific config card
 *   - Rich parameter editor (replaces sysadmin's previous raw JSON Schema textareas)
 *   - Policy radio
 *   - Side effects + advanced JSON pane (collapsible)
 *   - Right-rail Tool Helper (scope='sysadmin')
 *   - Review card
 *
 * Differences vs tenant wizard:
 *   - Sysadmin owns the full source type matrix (6 vs 2)
 *   - Sysadmin slug supports dots/dashes (`system.lookup_vendor`)
 *   - Created tools land in `draft` status; Publish lifecycle lives on
 *     the detail page (`catalog.tools.$id.tsx`)
 *   - Source config is shown (sysadmin authors it); on tenant it is
 *     synthesised from method+url
 *
 * Phase 2 (PR-G) will fold this + tenant `tools.new.tsx` + tenant
 * detail Configuration tab into a single `<ToolsAuthoring scope>` shared
 * component. Phase 1 ships the feature surface first so the adapter
 * has structurally similar UIs to unify.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Plus,
  X,
  AlertCircle,
  Globe,
  Database,
  Network,
  Package,
  FunctionSquare,
  FileJson,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { PolicyBadge } from '@/components/studio/policy-badge';
import { HelperPanel } from '@/components/studio/helper-panel';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useCreateSysadminTool,
  type CreateToolInput,
  type ToolPolicy,
  type ToolSourceType,
} from '@/lib/use-sysadmin-tools';
import { POLICY_DESCRIPTIONS } from '@/components/tools-authoring-shared/tools-shared.constants';
import {
  validateSlugSysadmin,
  parseJsonTyped,
  tryParseJsonArrayOfStrings,
  validateUrlWithTemplates,
} from '@/components/tools-authoring-shared/tools-shared.helpers';
import {
  ToolParameterEditor,
  buildJsonSchemaFromParameters,
  someParameterHasBlankName,
  type ToolParameter,
} from '@/components/tools-authoring-shared/tool-parameter-editor';

export const Route = createFileRoute('/sysadmin/catalog/tools/new')({
  component: SysadminToolWizardPage,
});

// ─── Source-type display metadata ──────────────────────────────────────────

interface SourceTypeOption {
  value: ToolSourceType;
  label: string;
  description: string;
  Icon: typeof Globe;
}

const SOURCE_OPTIONS: readonly SourceTypeOption[] = [
  {
    value: 'http',
    label: 'HTTP Request',
    description: 'Plain HTTP endpoint with admin-defined parameters.',
    Icon: Globe,
  },
  {
    value: 'database',
    label: 'Database Query',
    description: 'Parameterized SQL query against a configured connection.',
    Icon: Database,
  },
  {
    value: 'mcp',
    label: 'MCP Server',
    description: 'Wired to an external MCP server registered in the MCP catalog.',
    Icon: Network,
  },
  {
    value: 'openapi',
    label: 'OpenAPI',
    description: 'Imported from an OpenAPI / Swagger spec.',
    Icon: FunctionSquare,
  },
  {
    value: 'builtin',
    label: 'Built-in',
    description: 'Native handler shipped with the platform. Use sparingly.',
    Icon: Package,
  },
  {
    value: 'custom_function',
    label: 'Custom Function',
    description: 'Wrapped tenant function. Source config points at a function ID.',
    Icon: FunctionSquare,
  },
] as const;

// ─── HTTP draft (when sourceType === 'http') ────────────────────────────────

interface HttpHeader {
  key: string;
  value: string;
}

interface HttpConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  baseUrl: string;
  headers: HttpHeader[];
  body: string;
}

const EMPTY_HTTP: HttpConfig = {
  method: 'GET',
  baseUrl: '',
  headers: [],
  body: '',
};

// ─── Database draft ─────────────────────────────────────────────────────────

interface DbConfig {
  connection: string;
  query: string;
}

const EMPTY_DB: DbConfig = { connection: '', query: '' };

// ─── Component ──────────────────────────────────────────────────────────────

function SysadminToolWizardPage() {
  const navigate = useNavigate();
  const create = useCreateSysadminTool();

  // Identity
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');

  // Source type
  const [sourceType, setSourceType] = useState<ToolSourceType>('http');

  // Per-source-type config
  const [http, setHttp] = useState<HttpConfig>(EMPTY_HTTP);
  const [db, setDb] = useState<DbConfig>(EMPTY_DB);
  const [mcpServerId, setMcpServerId] = useState('');
  const [openApiSpecUrl, setOpenApiSpecUrl] = useState('');
  const [builtinHandlerName, setBuiltinHandlerName] = useState('');
  const [functionId, setFunctionId] = useState('');

  // Parameters
  const [parameters, setParameters] = useState<ToolParameter[]>([]);

  // Policy
  const [policy, setPolicy] = useState<ToolPolicy>('auto_approve');

  // Advanced JSON
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [sideEffectsText, setSideEffectsText] = useState('[]');
  const [outputSchemaText, setOutputSchemaText] = useState('{}');
  const [sourceConfigOverrideText, setSourceConfigOverrideText] =
    useState('');

  // Inline errors
  const [slugError, setSlugError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  // ─── Derived validation ─────────────────────────────────────────────────

  const httpUrlError = useMemo<string | null>(() => {
    if (sourceType !== 'http') return null;
    return validateUrlWithTemplates(http.baseUrl);
  }, [sourceType, http.baseUrl]);

  const sourceConfigComplete = useMemo<boolean>(() => {
    switch (sourceType) {
      case 'http':
        return !!http.baseUrl.trim() && !httpUrlError;
      case 'database':
        return !!db.connection.trim() && !!db.query.trim();
      case 'mcp':
        return !!mcpServerId.trim();
      case 'openapi':
        return !!openApiSpecUrl.trim();
      case 'builtin':
        return !!builtinHandlerName.trim();
      case 'custom_function':
        return !!functionId.trim();
      default:
        return false;
    }
  }, [
    sourceType,
    http,
    db,
    mcpServerId,
    openApiSpecUrl,
    builtinHandlerName,
    functionId,
    httpUrlError,
  ]);

  const blankParam = someParameterHasBlankName(parameters);

  const canSave =
    !!slug.trim() &&
    !!displayName.trim() &&
    !!description.trim() &&
    sourceConfigComplete &&
    parameters.length > 0 &&
    !blankParam;

  // ─── Helpers ────────────────────────────────────────────────────────────

  const addHeader = () =>
    setHttp((prev) => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '' }],
    }));
  const updateHeader = (i: number, field: 'key' | 'value', val: string) =>
    setHttp((prev) => ({
      ...prev,
      headers: prev.headers.map((h, idx) =>
        idx === i ? { ...h, [field]: val } : h,
      ),
    }));
  const removeHeader = (i: number) =>
    setHttp((prev) => ({
      ...prev,
      headers: prev.headers.filter((_, idx) => idx !== i),
    }));

  function buildSourceConfig(): {
    ok: true;
    config: Record<string, unknown>;
  } | { ok: false; err: string } {
    // If the sysadmin pasted an override JSON, use that instead of the
    // form-derived shape. This is the escape hatch for source types
    // we don't have a visual editor for yet (or for advanced HTTP
    // tweaks the form doesn't expose).
    const override = sourceConfigOverrideText.trim();
    if (override) {
      const parsed = parseJsonTyped(override, 'Source config override');
      if (!parsed.ok) return parsed;
      if (
        !parsed.value ||
        typeof parsed.value !== 'object' ||
        Array.isArray(parsed.value)
      ) {
        return {
          ok: false,
          err: 'Source config override must be a JSON object.',
        };
      }
      return { ok: true, config: parsed.value as Record<string, unknown> };
    }
    switch (sourceType) {
      case 'http':
        return {
          ok: true,
          config: {
            method: http.method,
            baseUrl: http.baseUrl.trim(),
            headers: http.headers
              .filter((h) => h.key.trim())
              .reduce<Record<string, string>>((acc, h) => {
                acc[h.key.trim()] = h.value;
                return acc;
              }, {}),
            body: http.body.trim() || null,
          },
        };
      case 'database':
        return {
          ok: true,
          config: {
            connection: db.connection.trim(),
            query: db.query.trim(),
          },
        };
      case 'mcp':
        return {
          ok: true,
          config: { mcpServerId: mcpServerId.trim() },
        };
      case 'openapi':
        return {
          ok: true,
          config: { specUrl: openApiSpecUrl.trim() },
        };
      case 'builtin':
        return {
          ok: true,
          config: { handlerName: builtinHandlerName.trim() },
        };
      case 'custom_function':
        return {
          ok: true,
          config: { functionId: functionId.trim() },
        };
      default:
        return { ok: false, err: 'Unknown source type.' };
    }
  }

  const handleSave = async () => {
    if (!canSave) return;
    setSlugError(null);
    setUrlError(null);

    // Slug validation (sysadmin rules — dots/dashes allowed)
    const slugCheck = validateSlugSysadmin(slug);
    if (!slugCheck.ok) {
      setSlugError(slugCheck.err);
      toast.error('Invalid slug', { description: slugCheck.err });
      return;
    }

    // Source config (per-type, with optional override)
    const cfg = buildSourceConfig();
    if (!cfg.ok) {
      toast.error('Invalid source config', { description: cfg.err });
      return;
    }

    // Side effects (advanced)
    const sideEffectsP = tryParseJsonArrayOfStrings(
      sideEffectsText || '[]',
      'Side effects',
    );
    if (!sideEffectsP.ok) {
      toast.error('Invalid side effects', { description: sideEffectsP.err });
      return;
    }

    // Output schema (advanced)
    const outputSchemaP = parseJsonTyped(
      outputSchemaText || '{}',
      'Output schema',
    );
    if (!outputSchemaP.ok) {
      toast.error('Invalid output schema', {
        description: outputSchemaP.err,
      });
      return;
    }

    // Build input schema from the rich parameter editor
    const inputSchema = buildJsonSchemaFromParameters(parameters);

    const payload: CreateToolInput = {
      slug: slugCheck.slug,
      displayName: displayName.trim(),
      description: description.trim(),
      sourceType,
      sourceConfig: cfg.config,
      inputSchema,
      outputSchema: outputSchemaP.value as Record<string, unknown>,
      policy,
      sideEffects: sideEffectsP.value,
      category: category.trim() || 'general',
      mcpServerId:
        sourceType === 'mcp' && mcpServerId.trim() ? mcpServerId.trim() : null,
    };

    try {
      const result = await create.mutateAsync(payload);
      toast.success(`Created "${result.data.slug}" (status: draft)`);
      // Navigate to the new tool's detail page so the user can verify
      // config and proceed through the publish workflow.
      navigate({
        to: '/sysadmin/catalog/tools/$id',
        params: { id: result.data.id },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // Heuristic — surface duplicate-slug + bad-URL inline on the form
      // (mirrors tenant wizard pattern). The sysadmin API doesn't yet
      // emit typed error codes, so we string-match.
      if (/duplicate|already exists|UNIQUE/i.test(msg)) {
        setSlugError('Slug already exists in the system catalog.');
      } else if (/invalid url|baseUrl/i.test(msg)) {
        setUrlError(msg);
      }
      toast.error('Create failed', { description: msg });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate({ to: '/sysadmin/catalog/tools' })}
            aria-label="Back to tools catalog"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div>
            <h1 className="text-lg font-semibold">New System Tool</h1>
            <p className="text-xs text-muted-foreground">
              Sysadmin-authored tool. Lands in <code className="font-mono text-[10px]">draft</code> — publish via the detail page once verified.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/sysadmin/catalog/tools' })}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSave || create.isPending}
            onClick={handleSave}
            data-testid="sysadmin-tool-create-submit"
          >
            {create.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </div>
      </div>

      {/* Two-column: form on left, Tool Helper on right */}
      <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
        <div className="overflow-auto p-6 max-w-3xl space-y-6">
          {/* Identity */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Identity</CardTitle>
              <CardDescription className="text-xs">
                Slug is the catalog upsert key — immutable after create.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Slug<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Input
                  placeholder="system.lookup_vendor"
                  value={slug}
                  onChange={(e) => {
                    setSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9_.-]/g, '-'),
                    );
                    if (slugError) setSlugError(null);
                  }}
                  className={cn(
                    'font-mono text-xs h-8',
                    slugError &&
                      'border-destructive focus-visible:ring-destructive',
                  )}
                  aria-invalid={slugError ? true : undefined}
                  aria-describedby={slugError ? 'tool-slug-error' : undefined}
                  data-testid="sysadmin-tool-slug-input"
                />
                {slugError && (
                  <p
                    id="tool-slug-error"
                    className="text-[11px] text-destructive flex items-start gap-1"
                  >
                    <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{slugError}</span>
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Lowercase alphanumeric with dots, dashes, or underscores.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Display name<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Input
                  placeholder="Lookup Vendor"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  data-testid="sysadmin-tool-name-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Description<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Textarea
                  placeholder="What this tool does, when an agent should call it, what it returns…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  maxLength={500}
                  data-testid="sysadmin-tool-desc-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input
                  placeholder="e.g., assets, telemetry, integration"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  maxLength={50}
                />
              </div>
            </CardContent>
          </Card>

          {/* Source type */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Source Type</CardTitle>
              <CardDescription className="text-xs">
                How this tool executes when an agent calls it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={sourceType}
                onValueChange={(v) => setSourceType(v as ToolSourceType)}
                className="space-y-2"
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                      sourceType === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/30',
                    )}
                  >
                    <RadioGroupItem
                      value={opt.value}
                      className="mt-0.5"
                      data-testid={`sysadmin-tool-source-${opt.value}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <opt.Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {opt.description}
                      </p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Source-specific config */}
          {sourceType === 'http' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">HTTP Configuration</CardTitle>
                <CardDescription className="text-xs">
                  Use {'{{variable}}'} syntax for dynamic values pulled from
                  agent input parameters.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-28">
                    <Label className="text-xs">Method</Label>
                    <Select
                      value={http.method}
                      onValueChange={(v) =>
                        setHttp({ ...http, method: v as HttpConfig['method'] })
                      }
                    >
                      <SelectTrigger aria-label="HTTP method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">
                      URL<span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      placeholder="https://api.example.com/v1/{{resource}}"
                      value={http.baseUrl}
                      onChange={(e) => {
                        setHttp({ ...http, baseUrl: e.target.value });
                        if (urlError) setUrlError(null);
                      }}
                      className={cn(
                        'font-mono',
                        (httpUrlError || urlError) &&
                          'border-destructive focus-visible:ring-destructive',
                      )}
                      aria-invalid={
                        httpUrlError || urlError ? true : undefined
                      }
                      aria-describedby={
                        httpUrlError || urlError ? 'tool-url-error' : undefined
                      }
                      data-testid="sysadmin-tool-url-input"
                    />
                    {(httpUrlError || urlError) && (
                      <p
                        id="tool-url-error"
                        className="text-[11px] text-destructive flex items-start gap-1 mt-1"
                      >
                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                        <span>{httpUrlError ?? urlError}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Headers</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={addHeader}
                    >
                      <Plus className="h-3 w-3" /> Add Header
                    </Button>
                  </div>
                  {http.headers.map((header, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        placeholder="Header name"
                        value={header.key}
                        onChange={(e) => updateHeader(i, 'key', e.target.value)}
                        className="font-mono text-xs flex-1"
                      />
                      <Input
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) =>
                          updateHeader(i, 'value', e.target.value)
                        }
                        className="font-mono text-xs flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => removeHeader(i)}
                        aria-label="Remove header"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>

                {(http.method === 'POST' ||
                  http.method === 'PUT' ||
                  http.method === 'PATCH') && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Request Body</Label>
                    <Textarea
                      placeholder={'{\n  "key": "{{value}}"\n}'}
                      value={http.body}
                      onChange={(e) =>
                        setHttp({ ...http, body: e.target.value })
                      }
                      rows={5}
                      className="font-mono text-xs"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {sourceType === 'database' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Database Configuration</CardTitle>
                <CardDescription className="text-xs">
                  Parameterized SQL — use {'{{variable}}'} placeholders bound
                  to the input parameters below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Connection<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    placeholder="e.g., warehouse-readonly"
                    value={db.connection}
                    onChange={(e) =>
                      setDb({ ...db, connection: e.target.value })
                    }
                    className="font-mono text-xs h-8"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Connection id from the platform Connections registry.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    SQL Query<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Textarea
                    placeholder={
                      "SELECT *\nFROM assets\nWHERE tag = '{{assetTag}}'"
                    }
                    value={db.query}
                    onChange={(e) => setDb({ ...db, query: e.target.value })}
                    rows={6}
                    className="font-mono text-xs"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {sourceType === 'mcp' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">MCP Server Binding</CardTitle>
                <CardDescription className="text-xs">
                  Reference an MCP server registered in the platform MCP catalog.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    MCP Server ID<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    placeholder="mcp-server-uuid"
                    value={mcpServerId}
                    onChange={(e) => setMcpServerId(e.target.value)}
                    className="font-mono text-xs h-8"
                    data-testid="sysadmin-tool-mcp-server-id"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {sourceType === 'openapi' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">OpenAPI Spec</CardTitle>
                <CardDescription className="text-xs">
                  Reference an OpenAPI / Swagger document.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Spec URL<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    placeholder="https://example.com/openapi.json"
                    value={openApiSpecUrl}
                    onChange={(e) => setOpenApiSpecUrl(e.target.value)}
                    className="font-mono text-xs h-8"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {sourceType === 'builtin' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Built-in Handler</CardTitle>
                <CardDescription className="text-xs">
                  Reference a native handler shipped with the platform.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Handler name<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    placeholder="e.g., search_asset_v1"
                    value={builtinHandlerName}
                    onChange={(e) => setBuiltinHandlerName(e.target.value)}
                    className="font-mono text-xs h-8"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {sourceType === 'custom_function' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Custom Function</CardTitle>
                <CardDescription className="text-xs">
                  Bind this tool to a deployed custom function.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Function ID<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    placeholder="function-uuid"
                    value={functionId}
                    onChange={(e) => setFunctionId(e.target.value)}
                    className="font-mono text-xs h-8"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Parameters — rich editor */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Input Parameters</CardTitle>
              <CardDescription className="text-xs">
                Define the inputs this tool accepts. At least one is required.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ToolParameterEditor
                parameters={parameters}
                onChange={setParameters}
                requireAtLeastOne
              />
            </CardContent>
          </Card>

          {/* Policy */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Execution Policy</CardTitle>
              <CardDescription className="text-xs">
                Default approval policy when an agent calls this tool. Tenants
                can override on assignment.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={policy}
                onValueChange={(v) => setPolicy(v as ToolPolicy)}
                className="space-y-2"
              >
                {(
                  ['auto_approve', 'require_approval', 'forbidden'] as const
                ).map((p) => (
                  <label
                    key={p}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                      policy === p
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/30',
                    )}
                  >
                    <RadioGroupItem
                      value={p}
                      className="mt-0.5"
                      data-testid={`sysadmin-tool-policy-${p}`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium capitalize">
                          {p.replace(/_/g, ' ')}
                        </span>
                        <PolicyBadge policy={p} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {POLICY_DESCRIPTIONS[p]}
                      </p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Advanced (side effects, output schema, source config override) */}
          <Card>
            <CardHeader
              className="pb-3 cursor-pointer select-none"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    {advancedOpen ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    Advanced (optional)
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Side effects, output schema, raw source config override.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-[10px] gap-1">
                  <FileJson className="h-2.5 w-2.5" /> JSON
                </Badge>
              </div>
            </CardHeader>
            {advancedOpen && (
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Side effects (JSON array of strings)
                  </Label>
                  <Input
                    value={sideEffectsText}
                    onChange={(e) => setSideEffectsText(e.target.value)}
                    placeholder='["read"]'
                    className="font-mono text-xs h-8"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Drives policy + audit. Examples:{' '}
                    <code className="font-mono">["read"]</code>,{' '}
                    <code className="font-mono">["write", "billable"]</code>.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Output schema (JSON Schema)</Label>
                  <Textarea
                    value={outputSchemaText}
                    onChange={(e) => setOutputSchemaText(e.target.value)}
                    rows={4}
                    className="font-mono text-xs"
                    spellCheck={false}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Source config override (JSON, sysadmin-only)
                  </Label>
                  <Textarea
                    value={sourceConfigOverrideText}
                    onChange={(e) =>
                      setSourceConfigOverrideText(e.target.value)
                    }
                    rows={5}
                    placeholder="(leave blank to use the visual config above)"
                    className="font-mono text-xs"
                    spellCheck={false}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Pasted JSON overrides the visual editor entirely. Use for
                    advanced fields not exposed by the form.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Review */}
          {canSave && (
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Review</CardTitle>
                <CardDescription className="text-xs">
                  Verify before creating. Tool lands in{' '}
                  <code className="font-mono">draft</code> status — publish
                  via the detail page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Slug
                    </p>
                    <p className="font-mono text-xs">{slug}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Display name
                    </p>
                    <p>{displayName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Source
                    </p>
                    <p className="capitalize">{sourceType.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Policy
                    </p>
                    <PolicyBadge policy={policy} />
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Parameters
                    </p>
                    <p>
                      {parameters.length} (
                      {parameters.filter((p) => p.required).length} required)
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">
                      Category
                    </p>
                    <p>{category || 'general'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column — Tool Helper (scope=sysadmin) */}
        <aside className="border-l md:border-l border-t md:border-t-0 overflow-hidden min-h-[400px]">
          <HelperPanel
            kind="tool_helper"
            scope="sysadmin"
            intro="Describe the tool you want and I'll help draft the manifest — slug, schemas, policy, side effects. I see the system catalog metadata only; I don't run code."
            className="h-full rounded-none border-0"
          />
        </aside>
      </div>
    </div>
  );
}
